'use client';

import React, { useState } from 'react';

const SUBMIT_URL = 'https://app.superpowerresume.com/api/pro-services';

interface FormState {
  name: string;
  email: string;
  targetRoles: string;
  location: string;
  applications: string;
  message: string;
  // Honeypot — kept empty by humans, filled by bots.
  company: string;
}

const EMPTY: FormState = {
  name: '',
  email: '',
  targetRoles: '',
  location: '',
  applications: '',
  message: '',
  company: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ProServicesForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [fieldError, setFieldError] = useState('');

  const update = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setStatus('idle');
    setFieldError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation.
    if (!form.name.trim()) {
      setFieldError('Please tell us your name.');
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setFieldError('Please enter a valid email address.');
      return;
    }
    if (!form.targetRoles.trim()) {
      setFieldError('Let us know which roles or titles you are targeting.');
      return;
    }

    setStatus('loading');
    setFieldError('');
    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          targetRoles: form.targetRoles.trim(),
          location: form.location.trim(),
          applications: form.applications.trim(),
          message: form.message.trim(),
          company: form.company, // honeypot
        }),
      });
      const data = (await res.json()) as { success?: boolean; message?: string; error?: string };
      if (res.ok && data.success) {
        setStatus('success');
        setMessage(data.message || 'Thanks! Our team will reach out shortly.');
        setForm(EMPTY);
      } else {
        setStatus('error');
        setMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again, or email doneforyou@superpowerresume.com.');
    }
  };

  const inputClasses =
    'w-full rounded-xl border border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20';
  const labelClasses = 'block text-sm font-medium text-gray-700 mb-2';

  if (status === 'success') {
    return (
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-lg font-semibold text-gray-900">{message}</p>
        <p className="text-sm text-gray-500 mt-1">
          We sent a confirmation to your inbox. Expect a reply within one business day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="w-full">
      {/* Honeypot field — visually hidden, ignored by humans. */}
      <div className="hidden" aria-hidden="true">
        <label htmlFor="company">Company (leave blank)</label>
        <input
          id="company"
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.company}
          onChange={update('company')}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className={labelClasses}>Name</label>
          <input
            id="name"
            type="text"
            value={form.name}
            onChange={update('name')}
            placeholder="Alex Johnson"
            required
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="email" className={labelClasses}>Email</label>
          <input
            id="email"
            type="email"
            value={form.email}
            onChange={update('email')}
            placeholder="you@example.com"
            required
            className={inputClasses}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="targetRoles" className={labelClasses}>Target roles / titles</label>
        <input
          id="targetRoles"
          type="text"
          value={form.targetRoles}
          onChange={update('targetRoles')}
          placeholder="e.g. Senior Product Manager, Director of Product"
          required
          className={inputClasses}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="location" className={labelClasses}>Location / remote preference</label>
          <input
            id="location"
            type="text"
            value={form.location}
            onChange={update('location')}
            placeholder="e.g. Remote (US), or Austin, TX"
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="applications" className={labelClasses}># of applications wanted</label>
          <input
            id="applications"
            type="text"
            value={form.applications}
            onChange={update('applications')}
            placeholder="e.g. 25 per week"
            className={inputClasses}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="proMessage" className={labelClasses}>
          Anything else? <span className="text-gray-400 font-normal">(notes, timeline, résumé link)</span>
        </label>
        <textarea
          id="proMessage"
          rows={4}
          value={form.message}
          onChange={update('message')}
          placeholder="Tell us about your goals, target companies, or paste a link to your résumé."
          className={inputClasses}
        />
      </div>

      {fieldError && <p className="text-sm text-red-500 mt-3">{fieldError}</p>}
      {status === 'error' && <p className="text-sm text-red-500 mt-3">{message}</p>}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="mt-6 w-full rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 px-6 py-3.5 text-base font-semibold text-white shadow transition-colors disabled:opacity-50"
      >
        {status === 'loading' ? 'Sending...' : 'Request My Done-For-You Plan'}
      </button>

      <p className="text-xs text-gray-400 mt-4 text-center">
        We&apos;ll only use your details to scope your job search. No spam, ever.
      </p>
    </form>
  );
}
